if (!customElements.get('siamaya-awards-carousel')) {
  customElements.define('siamaya-awards-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.track = this.querySelector('[data-awards-track]');
      this.slides = Array.from(this.querySelectorAll('.award-slide'));
      this.controls = this.querySelector('[data-awards-controls]');
      this.previous = this.querySelector('[data-awards-previous]');
      this.next = this.querySelector('[data-awards-next]');
      this.toggle = this.querySelector('[data-awards-toggle]');
      this.status = this.querySelector('[data-awards-status]');
      if (!this.track || !this.slides.length || !this.controls || !this.previous || !this.next || !this.toggle || !this.status) return;
      this.events = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches;
      this.inView = false;
      this.hovered = false;
      const options = { signal: this.events.signal };
      this.previous.addEventListener('click', () => this.navigate(-1), options);
      this.next.addEventListener('click', () => this.navigate(1), options);
      this.toggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.schedule();
      }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => this.schedule(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.events && this.schedule()), options);
      this.track.addEventListener('pointerdown', () => this.pause(), options);
      this.track.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.pause();
      }, { ...options, passive: true });
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.update(), 100);
      }, { ...options, passive: true });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.userPaused = true;
        this.schedule();
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.resizeObserver = new ResizeObserver(() => { this.update(); this.schedule(); });
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.6;
        this.schedule();
      }, { threshold: [0, 0.6] });
      this.intersectionObserver.observe(this);
      this.controls.hidden = false;
      this.update();
      this.schedule();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      clearTimeout(this.scrollTimer);
      clearTimeout(this.rotationTimer);
    }

    metrics() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = this.slides[0].getBoundingClientRect().width + gap;
      const visible = Math.max(1, Math.round((this.track.clientWidth + gap) / step));
      const lastStart = Math.max(0, this.slides.length - visible);
      const first = Math.max(0, Math.min(lastStart, Math.round(this.track.scrollLeft / step)));
      return { step, visible, lastStart, first };
    }

    update() {
      const { visible, first } = this.metrics();
      const last = Math.min(this.slides.length, first + visible);
      const template = visible === 1 ? this.status.dataset.single : this.status.dataset.range;
      const text = template.replace('__first__', String(first + 1)).replace('__last__', String(last)).replace('__total__', String(this.slides.length));
      if (this.status.textContent !== text) this.status.textContent = text;
    }

    schedule() {
      clearTimeout(this.rotationTimer);
      const focused = this.contains(document.activeElement);
      const enabled = !this.userPaused;
      const rotating = enabled && this.inView && !this.hovered && !focused && !document.hidden;
      this.toggle.setAttribute('aria-label', enabled ? this.toggle.dataset.pause : this.toggle.dataset.play);
      this.toggle.querySelector('[data-awards-pause-icon]').hidden = !enabled;
      this.toggle.querySelector('[data-awards-play-icon]').hidden = enabled;
      this.status.setAttribute('aria-live', rotating ? 'off' : 'polite');
      this.dataset.rotation = rotating ? 'playing' : 'paused';
      if (rotating) {
        this.rotationTimer = setTimeout(() => {
          this.move(1, true);
          this.schedule();
        }, 6000);
      }
    }

    pause() {
      this.userPaused = true;
      this.schedule();
    }

    navigate(direction) {
      this.pause();
      this.move(direction);
    }

    move(direction, automatic = false) {
      const { step, visible, lastStart, first } = this.metrics();
      let next = first + direction * (automatic ? 1 : visible);
      if (direction > 0) next = first >= lastStart ? 0 : Math.min(lastStart, next);
      else next = first <= 0 ? lastStart : Math.max(0, next);
      const behavior = this.motion.matches || (first >= lastStart && next === 0) || (first === 0 && next === lastStart) ? 'auto' : 'smooth';
      this.track.scrollTo({ left: next * step, behavior });
    }
  });
}
